import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, Mail, Loader2, ShieldCheck, KeyRound, ArrowLeft, Eye, EyeOff, Smartphone } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";
import { CONTACT } from "@/lib/contact";

const errMsg = (err, fallback) => {
  if (err?.response?.status === 429) return "Too many attempts. Please wait a bit and try again.";
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  return fallback;
};

const CodeInput = ({ value, onChange, testId }) => (
  <Input
    inputMode="numeric"
    maxLength={6}
    data-testid={testId}
    value={value}
    onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 6))}
    placeholder="Enter 6-digit code"
    className="h-14 text-center text-2xl tracking-[0.4em] font-display font-bold rounded-2xl border-slate-200 focus-visible:ring-ark-sky placeholder:text-base placeholder:tracking-normal placeholder:font-body placeholder:font-normal"
    autoFocus
  />
);

const AdminLogin = () => {
  const navigate = useNavigate();
  // steps: password | enroll | verify | forgot | reset
  const [step, setStep] = useState("password");
  const [loading, setLoading] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);

  // 2FA state
  const [mfaToken, setMfaToken] = useState("");
  const [code, setCode] = useState("");
  const [qr, setQr] = useState("");
  const [manualKey, setManualKey] = useState("");

  // forgot/reset state
  const [resetCode, setResetCode] = useState("");
  const [newPwd, setNewPwd] = useState("");

  const finishLogin = (data) => {
    localStorage.setItem("la_admin_token", data.access_token);
    localStorage.setItem("la_admin_email", data.email);
    localStorage.setItem("la_admin_role", data.role || "staff");
    localStorage.setItem("la_admin_name", data.name || data.email);
    toast.success("Welcome back!");
    navigate("/admin/dashboard");
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.mfa_required) {
        setMfaToken(data.mfa_token);
        setCode("");
        if (data.needs_enrollment) {
          setQr(data.qr_code);
          setManualKey(data.manual_key);
          setStep("enroll");
        } else {
          setStep("verify");
        }
      } else if (data.access_token) {
        finishLogin(data);
      }
    } catch (err) {
      toast.error(errMsg(err, "Login failed. Check your credentials."));
    } finally {
      setLoading(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    if (code.length !== 6) {
      toast.error("Enter the 6-digit code from your authenticator app.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-2fa", { mfa_token: mfaToken, code });
      finishLogin(data);
    } catch (err) {
      toast.error(errMsg(err, "Invalid code. Please try again."));
      // Only bounce back to password if the short-lived MFA session itself died.
      if (err?.response?.status === 401 && /session/i.test(err?.response?.data?.detail || "")) {
        setStep("password");
        setCode("");
      }
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("If an account exists, a reset code has been emailed.");
      setResetCode("");
      setNewPwd("");
      setStep("reset");
    } catch (err) {
      toast.error(errMsg(err, "Could not start password reset."));
    } finally {
      setLoading(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (resetCode.length !== 6) {
      toast.error("Enter the 6-digit code from your email.");
      return;
    }
    if (newPwd.length < 6) {
      toast.error("New password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { email, code: resetCode, new_password: newPwd });
      toast.success("Password updated. Please sign in.");
      setPassword("");
      setStep("password");
    } catch (err) {
      toast.error(errMsg(err, "Could not reset password."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-teal-50 flex items-center justify-center px-5 py-10">
      <div className="w-full max-w-md">
        <Link to="/" data-testid="back-home-link" className="flex items-center gap-3 mb-8 group">
          <img src={CONTACT.logoUrl} alt="Little Ark" className="h-14 w-auto object-contain" />
          <div className="text-[11px] uppercase tracking-[0.18em] text-ark-skyDeep font-bold">
            Admin Portal
          </div>
        </Link>

        <Card className="border-0 rounded-4xl shadow-ark-soft">
          <CardContent className="p-8">
            {step === "password" && (
              <>
                <h1 className="font-display font-bold text-2xl text-ark-ink">Welcome back</h1>
                <p className="text-sm text-slate-500 mt-1">Sign in to manage admissions inquiries.</p>
                <form onSubmit={submitPassword} className="mt-6 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="font-semibold text-slate-700">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input id="email" type="email" required data-testid="admin-email-input"
                        value={email} onChange={(e) => setEmail(e.target.value)}
                        placeholder="admin@littleark.in"
                        className="h-12 pl-11 rounded-2xl border-slate-200 focus-visible:ring-ark-sky" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password" className="font-semibold text-slate-700">Password</Label>
                      <button type="button" data-testid="forgot-password-link"
                        onClick={() => setStep("forgot")}
                        className="text-xs font-semibold text-ark-skyDeep hover:underline">
                        Forgot password?
                      </button>
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input id="password" type={showPwd ? "text" : "password"} required data-testid="admin-password-input"
                        value={password} onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        className="h-12 pl-11 pr-11 rounded-2xl border-slate-200 focus-visible:ring-ark-sky" />
                      <button type="button" onClick={() => setShowPwd((v) => !v)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                        {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" disabled={loading} data-testid="admin-login-btn"
                    className="w-full h-12 rounded-full bg-ark-sky hover:bg-ark-skyDeep text-white font-display font-semibold shadow-ark-sky">
                    {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Signing in…</> : "Continue"}
                  </Button>
                </form>
              </>
            )}

            {step === "enroll" && (
              <>
                <div className="h-12 w-12 rounded-2xl bg-sky-100 flex items-center justify-center">
                  <Smartphone className="h-6 w-6 text-ark-skyDeep" />
                </div>
                <h1 className="mt-4 font-display font-bold text-2xl text-ark-ink">Set up 2-step login</h1>
                <p className="text-sm text-slate-500 mt-1">
                  Scan this QR in Google Authenticator or Authy, then enter the 6-digit code to activate.
                </p>
                {qr && (
                  <div className="mt-5 flex justify-center">
                    <img src={qr} alt="Authenticator QR code" data-testid="totp-qr"
                      className="h-44 w-44 rounded-2xl border border-slate-200 p-2 bg-white" />
                  </div>
                )}
                {manualKey && (
                  <p className="mt-3 text-center text-xs text-slate-500">
                    Can't scan? Enter this key manually:<br />
                    <span data-testid="totp-manual-key" className="font-mono font-semibold text-slate-700 break-all">{manualKey}</span>
                  </p>
                )}
                <form onSubmit={submitCode} className="mt-5 space-y-4">
                  <CodeInput value={code} onChange={setCode} testId="totp-code-input" />
                  <Button type="submit" disabled={loading} data-testid="totp-verify-btn"
                    className="w-full h-12 rounded-full bg-ark-sky hover:bg-ark-skyDeep text-white font-display font-semibold">
                    {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Verifying…</> : "Activate & Sign In"}
                  </Button>
                </form>
                <BackButton onClick={() => setStep("password")} />
              </>
            )}

            {step === "verify" && (
              <>
                <div className="h-12 w-12 rounded-2xl bg-teal-100 flex items-center justify-center">
                  <ShieldCheck className="h-6 w-6 text-ark-tealDeep" />
                </div>
                <h1 className="mt-4 font-display font-bold text-2xl text-ark-ink">Two-step verification</h1>
                <p className="text-sm text-slate-500 mt-1">
                  Enter the 6-digit code from your authenticator app.
                </p>
                <form onSubmit={submitCode} className="mt-5 space-y-4">
                  <CodeInput value={code} onChange={setCode} testId="totp-code-input" />
                  <Button type="submit" disabled={loading} data-testid="totp-verify-btn"
                    className="w-full h-12 rounded-full bg-ark-sky hover:bg-ark-skyDeep text-white font-display font-semibold">
                    {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Verifying…</> : "Verify & Sign In"}
                  </Button>
                </form>
                <BackButton onClick={() => setStep("password")} />
              </>
            )}

            {step === "forgot" && (
              <>
                <div className="h-12 w-12 rounded-2xl bg-amber-100 flex items-center justify-center">
                  <KeyRound className="h-6 w-6 text-amber-600" />
                </div>
                <h1 className="mt-4 font-display font-bold text-2xl text-ark-ink">Forgot password</h1>
                <p className="text-sm text-slate-500 mt-1">
                  Enter your account email and we'll send a 6-digit reset code.
                </p>
                <form onSubmit={submitForgot} className="mt-5 space-y-4">
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input type="email" required data-testid="forgot-email-input"
                      value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="h-12 pl-11 rounded-2xl border-slate-200 focus-visible:ring-ark-sky" />
                  </div>
                  <Button type="submit" disabled={loading} data-testid="forgot-submit-btn"
                    className="w-full h-12 rounded-full bg-ark-sky hover:bg-ark-skyDeep text-white font-display font-semibold">
                    {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</> : "Send Reset Code"}
                  </Button>
                </form>
                <BackButton onClick={() => setStep("password")} label="Back to sign in" />
              </>
            )}

            {step === "reset" && (
              <>
                <div className="h-12 w-12 rounded-2xl bg-emerald-100 flex items-center justify-center">
                  <KeyRound className="h-6 w-6 text-emerald-600" />
                </div>
                <h1 className="mt-4 font-display font-bold text-2xl text-ark-ink">Enter reset code</h1>
                <p className="text-sm text-slate-500 mt-1">
                  Check <span className="font-semibold text-slate-700">{email}</span> for the 6-digit code, then set a new password.
                </p>
                <form onSubmit={submitReset} className="mt-5 space-y-4">
                  <CodeInput value={resetCode} onChange={setResetCode} testId="reset-code-input" />
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input type={showPwd ? "text" : "password"} required data-testid="reset-newpwd-input"
                      value={newPwd} onChange={(e) => setNewPwd(e.target.value)}
                      placeholder="New password (min 6 chars)"
                      className="h-12 pl-11 pr-11 rounded-2xl border-slate-200 focus-visible:ring-ark-sky" />
                    <button type="button" onClick={() => setShowPwd((v) => !v)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <Button type="submit" disabled={loading} data-testid="reset-submit-btn"
                    className="w-full h-12 rounded-full bg-ark-teal hover:bg-ark-tealDeep text-white font-display font-semibold">
                    {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Updating…</> : "Set New Password"}
                  </Button>
                </form>
                <BackButton onClick={() => setStep("forgot")} label="Resend code" />
              </>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-500 mt-6">
          Authorized personnel only · Little Ark Preschool
        </p>
      </div>
    </div>
  );
};

const BackButton = ({ onClick, label = "Back" }) => (
  <button type="button" onClick={onClick} data-testid="login-back-btn"
    className="mt-4 mx-auto flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-ark-skyDeep">
    <ArrowLeft className="h-4 w-4" /> {label}
  </button>
);

export default AdminLogin;
